export const TARGET_SCOPES = ['scope1', 'scope2', 'scope3'];

export const TARGET_TRAJECTORIES = {
  LINEAR: 'linear',
  CUSTOM: 'custom',
};

export const TARGET_ERROR_CODES = {
  BASELINE_INVENTORY: 'baselineInventory',
  TARGET_YEAR: 'targetYear',
  TARGET_RANGE: 'targetRange',
  REDUCTION: 'reduction',
  MILESTONE_DUPLICATE: 'milestoneDuplicate',
  MILESTONE_RANGE: 'milestoneRange',
  MILESTONE_VALUE: 'milestoneValue',
  FINAL_MILESTONE: 'finalMilestone',
};

const MAX_TARGET_DURATION_YEARS = 50;
export const TARGET_YEAR_FORWARD_SPAN = 15;
export const DEFAULT_BASE_YEAR = 2025;
export const DEFAULT_TARGET_YEAR = 2030;
const DEFAULT_REDUCTION_PERCENT = 0;

const numericValue = (value) => {
  if (value === '') return null;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

/** Return the inventory value for a scope and fiscal year. */
export function baselineEmissionsFor(target, inventories) {
  const inventory = inventories.find(candidate => Number(candidate.year) === Number(target.baselineYear));
  const baselineEmissions = inventory?.[target.scope];
  return Number.isFinite(baselineEmissions) ? baselineEmissions : null;
}

/** Derive target-year emissions from a base value and reduction percentage. */
export function targetEmissionsForReduction(baselineEmissions, reductionPercent) {
  const reduction = numericValue(reductionPercent);
  if (!Number.isFinite(baselineEmissions) || reduction === null) return null;
  return baselineEmissions * (1 - (reduction / 100));
}

/** Derive target-year emissions for the linear trajectory. */
export function linearTargetYearEmissions(target, inventories) {
  const explicitTarget = numericValue(target.targetYearEmissions);
  if (explicitTarget !== null) return explicitTarget;
  const baselineEmissions = baselineEmissionsFor(target, inventories);
  const reductionPercent = numericValue(target.reductionPercent);
  if (baselineEmissions === null || reductionPercent === null) return null;
  return targetEmissionsForReduction(baselineEmissions, reductionPercent);
}

/** Derive a reduction percentage from baseline and target-year emissions. */
export function reductionPercentForEmissions(baselineEmissions, targetYearEmissions) {
  if (!Number.isFinite(baselineEmissions) || baselineEmissions <= 0) return null;
  if (!Number.isFinite(targetYearEmissions)) return null;
  return ((baselineEmissions - targetYearEmissions) / baselineEmissions) * 100;
}

/** Calculate the reduction rate from one generated annual target to the next. */
export function annualReductionPercent(previousTarget, currentTarget) {
  if (!Number.isFinite(previousTarget) || previousTarget <= 0) return null;
  if (!Number.isFinite(currentTarget)) return null;
  return ((previousTarget - currentTarget) / previousTarget) * 100;
}

/** Calculate a generated target's cumulative reduction against the base year. */
export function reductionFromBasePercent(baselineTarget, currentTarget) {
  return reductionPercentForEmissions(baselineTarget, currentTarget);
}

/** Return whether a fiscal year is before the FY currently being reviewed. */
export function fiscalYearIsHistorical(fiscalYear, selectedFiscalYear) {
  if (fiscalYear === '' || fiscalYear === null || fiscalYear === undefined
    || selectedFiscalYear === '' || selectedFiscalYear === null
    || selectedFiscalYear === undefined) return false;
  const normalizedFiscalYear = Number(fiscalYear);
  const normalizedSelectedFiscalYear = Number(selectedFiscalYear);
  return Number.isInteger(normalizedFiscalYear)
    && Number.isInteger(normalizedSelectedFiscalYear)
    && normalizedFiscalYear < normalizedSelectedFiscalYear;
}

/** Add display-ready annual change and fiscal-year markers to a target preview. */
export function annotateTargetPreview(preview, currentFiscalYear) {
  if (!preview) return null;
  const normalizedCurrentFiscalYear = Number(currentFiscalYear);
  const hasCurrentFiscalYear = Number.isInteger(normalizedCurrentFiscalYear);
  return {
    ...preview,
    rows: preview.rows.map((row, index, rows) => ({
      ...row,
      annualReductionPercent: index === 0
        ? null
        : annualReductionPercent(rows[index - 1].target, row.target),
      reductionFromBasePercent: reductionFromBasePercent(preview.baselineValue, row.target),
      isCurrent: hasCurrentFiscalYear && row.year === normalizedCurrentFiscalYear,
      isFinalTarget: row.year === preview.targetYear,
      isHistorical: hasCurrentFiscalYear
        && fiscalYearIsHistorical(row.year, normalizedCurrentFiscalYear),
    })),
  };
}

const normalizeMilestones = milestones => (Array.isArray(milestones) ? milestones : [])
  .map(milestone => ({
    year: milestone.year ?? milestone.fiscalYear ?? '',
    targetEmissions: milestone.targetEmissions ?? milestone.emissions ?? '',
  }));

/** Initialize one editable target while accepting the original linear shape. */
export function createTargetDraft(scope, target, inventories, requestedBaseYear = DEFAULT_BASE_YEAR) {
  const creatingWithoutYears = requestedBaseYear === '';
  const baselineYear = creatingWithoutYears ? '' : (Number(requestedBaseYear) || DEFAULT_BASE_YEAR);
  const targetYear = creatingWithoutYears ? '' : (Number(target?.targetYear) > Number(baselineYear)
    ? Number(target.targetYear)
    : Math.max(DEFAULT_TARGET_YEAR, Number(baselineYear) + 5));
  const reductionPercent = target?.reductionPercent ?? DEFAULT_REDUCTION_PERCENT;
  const draft = {
    scope,
    baselineYear,
    targetYear,
    pathwayMode: target?.pathwayMode === 'custom' ? 'custom' : 'automatic',
    annualReductionPercentages: target?.annualReductionPercentages && typeof target.annualReductionPercentages === 'object'
      ? { ...target.annualReductionPercentages }
      : null,
    trajectoryType: target?.trajectoryType === TARGET_TRAJECTORIES.CUSTOM
      ? TARGET_TRAJECTORIES.CUSTOM
      : TARGET_TRAJECTORIES.LINEAR,
    reductionPercent,
    targetYearEmissions: '',
    milestones: normalizeMilestones(target?.milestones).filter(milestone => (
      Number(milestone.year) > Number(baselineYear) && Number(milestone.year) <= Number(targetYear)
    )),
  };
  const baseline = baselineYear === '' ? null : baselineEmissionsFor(draft, inventories);
  const savedEndValue = Number(target?.baselineYear) === Number(baselineYear)
    ? numericValue(target?.targetYearEmissions)
    : null;
  draft.targetYearEmissions = savedEndValue
    ?? targetEmissionsForReduction(baseline, reductionPercent)
    ?? '';
  if (draft.trajectoryType === TARGET_TRAJECTORIES.CUSTOM && targetYear !== '') {
    const finalMilestone = draft.milestones.find(milestone => Number(milestone.year) === targetYear);
    if (finalMilestone) {
      draft.targetYearEmissions = finalMilestone.targetEmissions;
      draft.reductionPercent = reductionPercentForEmissions(baseline, Number(finalMilestone.targetEmissions));
    } else {
      draft.milestones.push({ year: targetYear, targetEmissions: draft.targetYearEmissions });
    }
  }
  return draft;
}

/** Initialize independent drafts for every required scope. */
export function createTargetDrafts(targets = {}, inventories = [], baseYear = DEFAULT_BASE_YEAR) {
  const drafts = Object.fromEntries(TARGET_SCOPES.map(scope => [
    scope,
    createTargetDraft(scope, targets[scope], inventories, baseYear),
  ]));
  const commonTargetYear = drafts.scope1.targetYear || '';
  return Object.fromEntries(TARGET_SCOPES.map(scope => [
    scope,
    updateDraftTargetYear(drafts[scope], commonTargetYear, inventories),
  ]));
}

const withUpdatedFinalMilestone = (target, targetEmissions) => {
  if (target.trajectoryType !== TARGET_TRAJECTORIES.CUSTOM) return target.milestones;
  const finalYear = Number(target.targetYear);
  const intermediateMilestones = target.milestones
    .filter(milestone => Number(milestone.year) !== finalYear);
  return [...intermediateMilestones, { year: finalYear, targetEmissions }];
};

/** Keep percentage and target-year emissions synchronized when percentage changes. */
export function updateDraftReduction(target, reductionPercent, inventories) {
  const targetYearEmissions = targetEmissionsForReduction(
    baselineEmissionsFor(target, inventories),
    reductionPercent,
  );
  return {
    ...target,
    reductionPercent,
    targetYearEmissions: targetYearEmissions ?? '',
    milestones: withUpdatedFinalMilestone(target, targetYearEmissions ?? ''),
  };
}

/** Keep percentage and target-year emissions synchronized when emissions change. */
export function updateDraftTargetEmissions(target, targetYearEmissions, inventories) {
  const parsedTarget = numericValue(targetYearEmissions);
  const reductionPercent = parsedTarget === null
    ? ''
    : reductionPercentForEmissions(baselineEmissionsFor(target, inventories), parsedTarget);
  return {
    ...target,
    reductionPercent: reductionPercent ?? '',
    targetYearEmissions,
    milestones: withUpdatedFinalMilestone(target, targetYearEmissions),
  };
}

/** Move the final target and final custom milestone to a shared target year. */
export function updateDraftTargetYear(target, targetYear, inventories) {
  const previousTargetYear = numericValue(target.targetYear);
  const normalizedTargetYear = numericValue(targetYear);
  const milestones = target.trajectoryType === TARGET_TRAJECTORIES.CUSTOM
    ? target.milestones
      .filter(milestone => (
        previousTargetYear === null || Number(milestone.year) !== previousTargetYear
      ))
      .filter(milestone => normalizedTargetYear === null || Number(milestone.year) <= normalizedTargetYear)
    : target.milestones;
  const nextTarget = { ...target, targetYear, milestones };
  if (nextTarget.trajectoryType !== TARGET_TRAJECTORIES.CUSTOM) return nextTarget;
  const baselineYear = numericValue(nextTarget.baselineYear);
  const hasValidFinalYear = Number.isInteger(normalizedTargetYear)
    && Number.isInteger(baselineYear)
    && normalizedTargetYear > baselineYear;
  // Keep an incomplete edit incomplete. Adding a synthetic FY0 final milestone while the
  // user clears the field makes validation misleading and can crash annual preview builders.
  if (!hasValidFinalYear) return nextTarget;
  return {
    ...nextTarget,
    milestones: withUpdatedFinalMilestone(
      nextTarget,
      linearTargetYearEmissions(nextTarget, inventories) ?? '',
    ),
  };
}

/** Seed Custom mode with its required final milestone when none exists. */
export function changeTargetTrajectory(target, trajectoryType, inventories) {
  if (trajectoryType !== TARGET_TRAJECTORIES.CUSTOM) {
    return { ...target, trajectoryType: TARGET_TRAJECTORIES.LINEAR };
  }
  const targetEmissions = linearTargetYearEmissions(target, inventories);
  const nextTarget = { ...target, trajectoryType: TARGET_TRAJECTORIES.CUSTOM };
  return {
    ...nextTarget,
    milestones: withUpdatedFinalMilestone(nextTarget, targetEmissions ?? ''),
  };
}

/** Derive the effective end reduction for either a Linear or Custom pathway. */
export function effectiveReductionPercentForTarget(target, inventories) {
  const baselineEmissions = baselineEmissionsFor(target, inventories);
  if (baselineEmissions === null) return null;
  if (target.trajectoryType !== TARGET_TRAJECTORIES.CUSTOM) {
    return reductionPercentForEmissions(
      baselineEmissions,
      linearTargetYearEmissions(target, inventories),
    );
  }
  const finalMilestone = target.milestones.find(
    milestone => Number(milestone.year) === Number(target.targetYear),
  );
  const finalEmissions = numericValue(finalMilestone?.targetEmissions);
  return finalEmissions === null
    ? null
    : reductionPercentForEmissions(baselineEmissions, finalEmissions);
}

/** Synchronize display-only target outcome fields after Custom milestones change. */
export function synchronizeCustomTargetOutcome(target, inventories) {
  if (target.trajectoryType !== TARGET_TRAJECTORIES.CUSTOM) return target;
  const finalMilestone = target.milestones.find(
    milestone => Number(milestone.year) === Number(target.targetYear),
  );
  const targetYearEmissions = numericValue(finalMilestone?.targetEmissions);
  if (targetYearEmissions === null) return target;
  return {
    ...target,
    targetYearEmissions,
    reductionPercent: effectiveReductionPercentForTarget(target, inventories) ?? '',
  };
}

const commonValidationErrors = (target, inventories) => {
  const baselineYear = numericValue(target.baselineYear);
  const targetYear = numericValue(target.targetYear);
  const errors = [];
  if (baselineEmissionsFor(target, inventories) === null) {
    errors.push(TARGET_ERROR_CODES.BASELINE_INVENTORY);
  }
  if (!Number.isInteger(targetYear) || !Number.isInteger(baselineYear) || targetYear <= baselineYear) {
    errors.push(TARGET_ERROR_CODES.TARGET_YEAR);
  } else if (targetYear - baselineYear > MAX_TARGET_DURATION_YEARS) {
    errors.push(TARGET_ERROR_CODES.TARGET_RANGE);
  }
  return errors;
};

const customValidationErrors = (target) => {
  const baselineYear = Number(target.baselineYear);
  const targetYear = Number(target.targetYear);
  const milestoneYears = target.milestones.map(milestone => Number(milestone.year));
  const errors = [];
  if (new Set(milestoneYears).size !== milestoneYears.length) {
    errors.push(TARGET_ERROR_CODES.MILESTONE_DUPLICATE);
  }
  if (target.milestones.some(milestone => (
    !Number.isInteger(Number(milestone.year))
    || Number(milestone.year) <= baselineYear
    || Number(milestone.year) > targetYear
  ))) errors.push(TARGET_ERROR_CODES.MILESTONE_RANGE);
  if (target.milestones.some(milestone => (
    numericValue(milestone.targetEmissions) === null || Number(milestone.targetEmissions) < 0
  ))) errors.push(TARGET_ERROR_CODES.MILESTONE_VALUE);
  if (!milestoneYears.includes(targetYear)) errors.push(TARGET_ERROR_CODES.FINAL_MILESTONE);
  return errors;
};

/** Return stable validation codes for one target draft. */
export function validateTargetDraft(target, inventories) {
  const errors = commonValidationErrors(target, inventories);
  if (target.trajectoryType === TARGET_TRAJECTORIES.CUSTOM) {
    return [...errors, ...customValidationErrors(target)];
  }
  const reductionPercent = numericValue(target.reductionPercent);
  if (reductionPercent === null || reductionPercent <= 0 || reductionPercent > 100) {
    errors.push(TARGET_ERROR_CODES.REDUCTION);
  }
  return errors;
}

const sortedMilestones = target => target.milestones
  .map(milestone => ({
    year: Number(milestone.year),
    targetEmissions: Number(milestone.targetEmissions),
  }))
  .sort((left, right) => left.year - right.year);

/** Build a year-by-year Custom pathway by interpolating between milestones. */
export function buildCustomTargetPreview(target, inventories) {
  if (validateTargetDraft(target, inventories).length > 0) return null;
  const baselineYear = Number(target.baselineYear);
  const targetYear = Number(target.targetYear);
  const baselineValue = baselineEmissionsFor(target, inventories);
  const milestones = sortedMilestones(target);
  const anchors = [{ year: baselineYear, targetEmissions: baselineValue }, ...milestones];
  const rows = [];
  for (let year = baselineYear; year <= targetYear; year += 1) {
    const nextAnchorIndex = anchors.findIndex(anchor => anchor.year >= year);
    const nextAnchor = anchors[nextAnchorIndex];
    const previousAnchor = anchors[Math.max(0, nextAnchorIndex - 1)];
    const duration = nextAnchor.year - previousAnchor.year;
    const progress = duration === 0 ? 0 : (year - previousAnchor.year) / duration;
    const targetValue = previousAnchor.targetEmissions
      + ((nextAnchor.targetEmissions - previousAnchor.targetEmissions) * progress);
    const inventory = inventories.find(candidate => candidate.year === year);
    const actual = inventory?.[target.scope] ?? null;
    rows.push({
      year,
      actual,
      target: targetValue,
      variance: actual === null ? null : actual - targetValue,
      isBaseline: year === baselineYear,
      isMilestone: milestones.some(milestone => milestone.year === year),
    });
  }
  const endTarget = milestones[milestones.length - 1].targetEmissions;
  return {
    scope: target.scope,
    baselineYear,
    targetYear,
    baselineValue,
    endTarget,
    annualChange: (endTarget - baselineValue) / (targetYear - baselineYear),
    rows,
  };
}

/** Return whether any Custom milestone rises above its preceding anchor. */
export function hasMilestoneIncrease(target, inventories) {
  const baselineEmissions = baselineEmissionsFor(target, inventories);
  if (baselineEmissions === null) return false;
  let previousEmissions = baselineEmissions;
  return sortedMilestones(target).some(milestone => {
    const isIncrease = milestone.targetEmissions > previousEmissions;
    previousEmissions = milestone.targetEmissions;
    return isIncrease;
  });
}

/** Build the annual preview for the currently selected Scope draft. */
export function buildTargetDraftPreview(
  target,
  inventories,
  buildLinearPreview,
  currentFiscalYear,
) {
  if (!target || validateTargetDraft(target, inventories).length > 0) return null;
  const preview = target.trajectoryType === TARGET_TRAJECTORIES.CUSTOM
    ? buildCustomTargetPreview(target, inventories)
    : buildLinearPreview(target, inventories);
  if (!preview) return null;
  return annotateTargetPreview({
    ...preview,
    effectiveReductionPercent: effectiveReductionPercentForTarget(target, inventories),
    rows: preview.rows.map(row => ({
      ...row,
      isMilestone: Boolean(row.isMilestone),
    })),
  }, currentFiscalYear);
}

/** Combine three valid scope previews into a single compact fiscal-year matrix. */
export function buildCombinedTargetPreview(drafts, inventories, buildLinearPreview) {
  const previews = Object.fromEntries(TARGET_SCOPES.map(scope => {
    const target = drafts[scope];
    const preview = buildTargetDraftPreview(target, inventories, buildLinearPreview);
    return [scope, preview];
  }));
  if (TARGET_SCOPES.some(scope => !previews[scope])) return null;

  const baselineYear = previews.scope1.baselineYear;
  const targetYear = previews.scope1.targetYear;
  const baseline = Object.fromEntries(TARGET_SCOPES.map(scope => [
    scope,
    previews[scope].baselineValue,
  ]));
  const baselineTotal = TARGET_SCOPES.reduce((total, scope) => total + baseline[scope], 0);
  const rows = previews.scope1.rows.map((baseRow, index) => {
    const values = Object.fromEntries(TARGET_SCOPES.map(scope => [
      scope,
      previews[scope].rows[index].target,
    ]));
    const total = TARGET_SCOPES.reduce((sum, scope) => sum + values[scope], 0);
    return {
      year: baseRow.year,
      ...values,
      total,
      reductions: {
        ...Object.fromEntries(TARGET_SCOPES.map(scope => [
          scope,
          reductionFromBasePercent(baseline[scope], values[scope]),
        ])),
        total: reductionFromBasePercent(baselineTotal, total),
      },
      isBaseline: baseRow.year === baselineYear,
      isFinalTarget: baseRow.year === targetYear,
      milestoneScopes: TARGET_SCOPES.filter(scope => previews[scope].rows[index].isMilestone),
    };
  });
  return { baseline, baselineTotal, baselineYear, targetYear, rows };
}

/** Convert a valid draft to the persisted target contract. */
export function serializeTargetDraft(target, inventories) {
  const commonFields = {
    scope: target.scope,
    trajectoryType: target.trajectoryType,
    baselineYear: Number(target.baselineYear),
    targetYear: Number(target.targetYear),
  };
  if (target.trajectoryType === TARGET_TRAJECTORIES.CUSTOM) {
    return { ...commonFields, milestones: sortedMilestones(target) };
  }
  return {
    ...commonFields,
      pathwayMode: target.pathwayMode || 'automatic',
    annualReductionPercentages: target.pathwayMode === 'custom'
      ? { ...(target.annualReductionPercentages || {}) }
      : null,
    reductionPercent: Number(target.reductionPercent),
    targetYearEmissions: numericValue(target.targetYearEmissions),
  };
}
